import { Component, Vue, Emit } from "vue-property-decorator";
import MyButton from "@/components/MyButton.vue";
import { AuthUtils } from '../../utils/authUtils';
import { MsalResult, AccessTokenStatus, ApiResult } from '../../enums';
import { AuthResult } from '../../models/authResult';
import { Consts } from '@/consts';
import { GraphApiUtils } from '@/utils/graphApiUtils';
import { UserUtils } from '@/utils/userUtils';
import { GroupUtils } from '@/utils/groupUtils';
import { Groups } from '@/models/groups';
import { AppConfig } from '@/app-config';
import { Group } from '@/models/group';
import { GraphApiResult } from '@/models/graphApiResult';
import { Users } from '@/models/users';
import { AppUtils } from '@/utils/appUtils';
@Component({
	components: {
		MyButton
	}
})
export default class AdminHome extends Vue {
	private title: string = '「地域人材育成のために」 管理画面';
	private caption: string = "管理画面では教材や提出物の管理をします。";
	private description: string =
		"メール宛に案内したOffice365アカウント情報にてログインしてください。";
	private buttonTitle: string = "はじめる";
	private buttonDisabled: boolean = true;
	private buttonClassName: string = "admin-home";

	@Emit("update-admin-home")
	public updateAdminHome(displayName: string, teams: Groups): void { }

	public async created(): Promise<void> {
		// ログイン画面ではアクセストークンのステータスに応じて新規取得、再取得、自動リダイレクトを行う。
		// 自動リダイレクトはAADのログイン状態によっては再ログイン操作が不要の場合がある。
		let accessTokenStatus = await GraphApiUtils.getAccessTokenStatus(Vue.$accessTokenInfo);
		switch (accessTokenStatus) {
			case AccessTokenStatus.EmptyOrUndefined:
				const authResult: AuthResult = await AuthUtils.acquireTokenSilent(Vue.$userAgentApplication);
				if (authResult.result !== MsalResult.Success) {
					AppUtils.removeAccessTokenInfo();
					this.buttonDisabled = false;
					return;
				}
				AppUtils.setAccessTokenInfo(authResult.accessTokenInfo);
				accessTokenStatus = AccessTokenStatus.Valid;
				break;
			case AccessTokenStatus.Expired:
			case AccessTokenStatus.AuthenticationError:
			case AccessTokenStatus.InvalidAuthenticationToken:
				// バックグラウンドでの取得を試みる。
				const silentResult: AuthResult = await AuthUtils.acquireTokenSilent(Vue.$userAgentApplication);
				if (silentResult.result !== MsalResult.Success) {
					// 無効なアクセストークンをストレージから削除し、その状態でリダイレクトによる認証を行う。
					AppUtils.removeAccessTokenInfo();
					const authRedirectResult: AuthResult = await AuthUtils.authenticateRedirect(Vue.$userAgentApplication);
					if (authRedirectResult.result !== MsalResult.RedirectingToLogin) {
						console.error(authRedirectResult);
						this.buttonDisabled = false;
					}
				} else {
					AppUtils.setAccessTokenInfo(silentResult.accessTokenInfo);
					accessTokenStatus = AccessTokenStatus.Valid;
				}
				break;
			default:
				break;
		}
		if (accessTokenStatus !== AccessTokenStatus.Valid) {
			this.buttonDisabled = false;
			return;
		}
		const accessToken: string = Vue.$accessTokenInfo.token;
		// Userの取得
		const userResult = await UserUtils.GetMe(accessToken);
		if (userResult.result !== ApiResult.Success) {
			this.buttonDisabled = false;
			return;
		}
		const user = userResult.value;
		if (user === null) {
			this.buttonDisabled = false;
			return;
		}
		// 「運営」セキュリティグループへの所属を確認
		// この画面ではログインそのものを取り消す必要があるが、取り急ぎローカルのアクセストークンのみを削除する。
		const adminMembersResult: GraphApiResult<Users> =
			await GroupUtils.GetMembers(accessToken, AppConfig.ADMIN_SECUTIRY_GROUP_ID);
		if (adminMembersResult.result !== ApiResult.Success) {
			AppUtils.removeAccessTokenInfo();
			this.buttonDisabled = false;
			alert('予期せぬエラーが発生したためログインできませんでした。');
			return;
		}
		const adminMembers: Users | null = adminMembersResult.value;
		if ((adminMembers === null) || (adminMembers.value.length === 0) || (!adminMembers.belongs(user.id))) {
			AppUtils.removeAccessTokenInfo();
			this.buttonDisabled = false;
			alert('無効なアカウントのためログインできませんでした。');
			// TODO このメッセージが表示されたアカウントは権限がないためAAD側のログアウト操作と適切アカウントでの再ログインが必要。
			// TODO 同等の機能が生徒側の画面で未所属のユーザーがアクセスした際にも実装が必要。
			// this.showAdminLogoutConfirmation('無効なアカウント', '無効なアカウントのためサービスにログインできませんでした。アカウントを切り替えますか？');
			return;
		}
		localStorage.setItem(Consts.LOCAL_STORAGE_KEY_USER_ID, user.id);
		localStorage.setItem(Consts.LOCAL_STORAGE_KEY_USER_DISPLAYNAME, user.displayName);

		const teamsResult = await GroupUtils.GetJoinedTeams(accessToken);
		if ((teamsResult.result !== ApiResult.Success) || (teamsResult.value === null)) {
			return;
		}
		const teams: Groups = teamsResult.value;
		const groupId: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_GROUPID);
		if ((groupId !== null) && GroupUtils.IsPermitted(groupId)) {
			// キャッシュを利用する
			const permittedTeams: Groups = GroupUtils.GetPermittedGroups(teams);
			if (permittedTeams.value.length > 0) {
				this.updateAdminHome(user.displayName, permittedTeams);
				this.$router.push(Consts.PATH_ADMIN_CHANNELS);
			} else {
				// メンバー外の可能性あり TODOメッセージ表示
				localStorage.removeItem(Consts.LOCAL_STORAGE_KEY_USER_GROUPID);
			}
		} else {
			const permittedTeams: Groups = GroupUtils.GetPermittedGroups(teams);
			if (permittedTeams.value.length > 0) {
				localStorage.setItem(Consts.LOCAL_STORAGE_KEY_USER_GROUPID, permittedTeams.value[0].id);
				this.updateAdminHome(user.displayName, permittedTeams);
				this.$router.push(Consts.PATH_ADMIN_CHANNELS);
			} else {
				// 管理権限設定ミスの可能性あり TODOメッセージ表示
			}
		}
	}

	public async onLoginButtonClick(e: Event): Promise<void> {
		// TODO ここでは明示的なログアウト操作を経ずにログインを試みるためサービス外でのユーザー情報の変更などを検知できない可能性がある。
		const authResult: AuthResult = await AuthUtils.authenticateRedirect(Vue.$userAgentApplication);
		if (authResult.result !== MsalResult.RedirectingToLogin) {
			alert('ログインできませんでした。');
			console.error(authResult);
			this.buttonDisabled = false;
			return;
		}
		this.buttonDisabled = true;
	}

	@Emit('show-admin-logout-confirmation')
	private showAdminLogoutConfirmation(title: string, message: string): void { }
}
